import { oauthService } from '@/services/api'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAlertStore } from './alert'
import { useBackendStore } from './backend'
import storeConfigs from 'constants/storeConfigs.ts'
import { useCookies } from '@vueuse/integrations/useCookies'

const COOKIE = 'auth'

export const useAuthStore = defineStore(
	storeConfigs.auth.name,
	() => {
		const auth = ref<Api.Auth | undefined>(getCookie())
		const isRefreshing = ref(false)

		const token = computed(() => {
			return auth.value?.access_token
		})

		function setCookie(
			auth: Api.OauthResponse & {
				rememberMe: boolean
				rememberMeValue?: number
			}
		) {
			const { url: domain } = useBackendStore()
			const isProduction = import.meta.env.PROD

			const authorized_at = Math.floor(Date.now() / 1000)
			//user.oauth.expires_in = user.oauth.auhorized_at + user.oauth.expires_in
			const value = auth.rememberMe
				? auth?.rememberMeValue
					? auth.rememberMeValue
					: 60 * 60 * 24 * 7
				: 60 * 60 * 24

			useCookies().set(
				COOKIE,
				{ ...auth, rememberMe: value, authorized_at },
				{
					// expires: auth.rememberMe,
					domain: isProduction ? '' : domain?.domain,
					secure: isProduction,
					sameSite: isProduction ? 'none' : 'lax',
				}
			)
		}

		function getCookie() {
			return useCookies().get<Api.Auth>(COOKIE)
		}

		async function removeCookie() {
			const { url: domain } = useBackendStore()
			const isProduction = import.meta.env.PROD

			useCookies().remove(COOKIE, {
				domain: isProduction ? '' : domain?.domain,
				secure: isProduction,
				sameSite: isProduction ? 'none' : 'lax',
			})
		}

		async function authenticate({
			username,
			password,
			rememberMe,
			force = false,
		}: Api.OauthAuthenticationInput & {
			force?: boolean
			rememberMe: boolean
		}) {
			try {
				const cookie = getCookie()
				if (cookie && !force) {
					auth.value = cookie
				}
				const { data } = await oauthService.authenticate({
					username,
					password,
				})
				auth.value = data
				setCookie({ ...data, rememberMe })
			} catch (error) {
				console.log('auth error', error)
				throw error
			}
		}

		async function refreshAuthentication() {
			isRefreshing.value = true
			const cookie = getCookie()

			try {
				const { data } = await oauthService.refresh({
					refresh_token: auth.value?.refresh_token ?? cookie.refresh_token,
				})

				auth.value = data

				setCookie({
					...data,
					rememberMe: cookie?.rememberMe !== 0,
					rememberMeValue: cookie?.rememberMe,
				})
			} catch (error) {
				useAlertStore().errorAlert(error)
			} finally {
				isRefreshing.value = false
			}
		}

		function $reset() {
			removeCookie()
			auth.value = undefined
		}

		// Re-read the auth cookie into the auth ref. Needed in Cypress test runs
		// because cy.setCookie() writes via CDP, bypassing universal-cookie's
		// change listener. Call this from the __cypress_hydrate_auth hook in
		// useUserStore() — within auth.ts's own scope so auth.value is writable.
		function loadFromCookie() {
			auth.value = getCookie()
		}

		return {
			auth,
			isRefreshing,
			token,
			setCookie,
			getCookie,
			removeCookie,
			loadFromCookie,
			authenticate,
			refreshAuthentication,
			$reset,
		}
	},
	{
		persist: storeConfigs.auth.persist,
	}
)

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
